import { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import { CaseSchema, GetAllCasesApiV1CasesGetData } from "@/api/generated/types.gen";
import { DataTableWithPagination } from "@/components/DataTables/DataTableWithPagination";
import { ActionButton } from "@/components/DataTables/types/data-table";
import { Button } from "@/components/ui/button";

import { CAPABILITIES } from "@constants/capabilities";
import { NAVIGATION_PATHS } from "@constants/navigation";

import { useCases } from "@hooks/queries/use-cases";
import { useCapabilities, useIsAdmin } from "@hooks/queries/use-me";

import { UpdateCaseModal } from "../UpdateCaseModal";
import { CreateCaseModal } from "./components/CreateCaseModal";
import { DeleteCaseConfirmation } from "./components/DeleteConfirmation";
import { DeleteCasesConfirmation } from "./components/DeleteMultipleConfirmation";
import { Filter } from "./components/Filter";
import { SpotlightSearch } from "./components/SpotlightSearch";
import { CaseListTableColumns, DoctorColumn } from "./constants/header";

type CasesListProps = {
  onRowClick?: (caseItem: CaseSchema) => void;
};

export const CasesList = ({ onRowClick }: CasesListProps) => {
  const navigate = useNavigate();
  const { can } = useCapabilities();
  const isAdmin = useIsAdmin();
  const [selectedCases, setSelectedCases] = useState<string[]>([]);
  const [filters, setFilters] = useState<GetAllCasesApiV1CasesGetData["query"]>({});
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchValue || undefined }));
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const { data: cases, isLoading } = useCases({
    ...filters,
    status: filters?.status ?? undefined,
    view_type: filters?.view_type ?? undefined,
  });

  const handleFiltersChange = (
    newFilters: GetAllCasesApiV1CasesGetData["query"]
  ) => {
    setFilters((prev) => ({ ...newFilters, search: prev?.search }));
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  // MDRS-230: the Doctor column (name + release state). Admin-only — a doctor
  // must never see the flag governing their own visibility. Read-only here;
  // assigning and releasing happen in the Update case modal.
  //
  // Inserted directly after Examinee Name per the design. The design's own order
  // (Doctor third, before Service type) doesn't map onto this table, which has no
  // Service type column and puts Examinee sixth — so the relative position is
  // preserved rather than reordering columns this ticket doesn't own.
  const columns = useMemo(() => {
    if (!isAdmin) return CaseListTableColumns;

    const examineeIndex = CaseListTableColumns.findIndex(
      (column) => column.id === "examinee_name"
    );
    if (examineeIndex === -1) return [...CaseListTableColumns, DoctorColumn];

    return [
      ...CaseListTableColumns.slice(0, examineeIndex + 1),
      DoctorColumn,
      ...CaseListTableColumns.slice(examineeIndex + 1),
    ];
  }, [isAdmin]);

  const actions: ActionButton<CaseSchema>[] = [
    {
      component: (caseItem) => <UpdateCaseModal caseItem={caseItem} />,
    },
    {
      component: (caseItem) => <DeleteCaseConfirmation caseItem={caseItem} />,
    },
  ];

  const loadingComponent = (
    <ThreeDots
      visible={true}
      height="60"
      width="60"
      color="#00BFFF"
      radius="9"
      ariaLabel="signing-out-loading"
    />
  );

  return (
    <div className="relative space-y-4">
      <div className="flex items-center gap-4">
        {selectedCases.length > 0 && (
          <DeleteCasesConfirmation
            caseIds={selectedCases}
            onCleared={() => setSelectedCases([])}
          />
        )}

        <SpotlightSearch value={searchValue} onChange={handleSearchChange} />

        <Filter onFiltersChange={handleFiltersChange} />

        {/* Case creation (access levels v1 + MDRS-157). Doctors get the
            template-based create modal; referrers get the "New case" page (the
            4-step wizard) — distinguished by REPORT_BUILDER (referrers have
            CASES_CREATE but not REPORT_BUILDER). */}
        {can(CAPABILITIES.REPORT_BUILDER) ? (
          <CreateCaseModal />
        ) : (
          can(CAPABILITIES.CASES_CREATE) && (
            <Button onClick={() => navigate({ to: NAVIGATION_PATHS.NEW_CASE })}>
              <Plus className="mr-2 h-4 w-4" />
              New case
            </Button>
          )
        )}
      </div>

      <DataTableWithPagination
        data={cases || []}
        columns={columns}
        isLoading={isLoading}
        selectable
        selectedItems={selectedCases}
        onSelectionChange={setSelectedCases}
        getItemId={(item) => item.id}
        onRowClick={onRowClick}
        className="rounded-2xl border border-foreground-muted/20 bg-background-white p-4"
        emptyMessage="No cases found"
        loadingComponent={loadingComponent}
        actionsHeaderLabel="Actions"
        actions={actions}
        showActions
        paginationOptions={{
          initialPageSize: 10,
          pageSizeOptions: [10, 20, 50],
        }}
      />
    </div>
  );
};
